<!-- EntriesFilterBar.vue -->
<template>
    <div class="filter-bar-wrap">
        <v-row>
        <v-col v-if="showSortToggle" cols="3" sm="1" md="1" lg="1">
            <v-btn
                icon
                size="large"
                elevation="3"
                variant="plain"
                @click="$emit('toggle-sort')"
                style="
                    width: 48px;
                    height: 48px;
                    min-width: 48px;
                    min-height: 48px;
                "
            >
                <v-icon>
                    {{
                        sortDirection === "desc"
                            ? "mdi-sort-clock-descending-outline"
                            : "mdi-sort-clock-ascending-outline"
                    }}
                </v-icon>
            </v-btn>
        </v-col>
        <v-col :cols="showSortToggle ? 9 : 12" :sm="showSortToggle ? 11 : 12" :md="showSortToggle ? 11 : 12" :lg="showSortToggle ? 11 : 12">
            <v-card
                class="filter-card"
                v-click-outside="{
                    handler: onClickOutside,
                    include: clickOutsideInclude,
                    closeConditional: () => isExpanded,
                }"
            >
                <v-toolbar
                    density="compact"
                    @click="isExpanded = !isExpanded"
                    class="cursor-pointer"
                    :color="isMobile && chips.length ? 'primary' : ''"
                >
                    <v-tooltip activator="parent" location="bottom">
                        {{ isExpanded ? closeTooltipText : openTooltipText }}
                    </v-tooltip>
                    <v-toolbar-title>{{
                        title || $t("entries.filters")
                    }}</v-toolbar-title>

                    <!-- CLEAR‑ALL X  (mobile & collapsed) -->
                    <template v-if="isMobile && chips.length && !isExpanded">
                        <v-spacer></v-spacer>
                        <v-btn
                            icon
                            density="comfortable"
                            elevation="0"
                            color="white"
                            @click.stop="resetFilters"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>

                    <template v-if="chips.length && !isMobile" #extension>
                        <div class="px-4 pb-2 d-flex flex-wrap ga-2" @click.stop>
                            <v-chip
                                v-for="chip in chips"
                                :key="chip.id"
                                closable
                                size="small"
                                color="primary"
                                @click.stop
                                @click:close="chip.clear()"
                            >
                                {{ chip.label }}
                            </v-chip>
                        </div>
                    </template>
                </v-toolbar>
                <div v-auto-animate>
                    <v-card-text v-if="isExpanded">
                        <v-row :class="variant === 'reservations' ? 'mb-2' : 'mb-4'" :dense="variant === 'reservations'">
                            <v-col cols="12" sm="12" md="6">
                                <FormLocation
                                    :modelValue="loadingFilter"
                                    formType="ware"
                                    :title="$t('entries.filterLoadingLocation')"
                                    @update:modelValue="handleLoadingFilter"
                                    :useAsFilter="true"
                                    :density="variant === 'reservations' ? 'compact' : undefined"
                                />
                            </v-col>
                            <v-col cols="12" sm="12" md="6">
                                <FormLocation
                                    :modelValue="unloadingFilter"
                                    formType="ware"
                                    :title="
                                        $t('entries.filterUnloadingLocation')
                                    "
                                    @update:modelValue="handleUnloadingFilter"
                                    :useAsFilter="true"
                                    :density="variant === 'reservations' ? 'compact' : undefined"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="variant !== 'reservations'" class="mb-4">
                            <v-divider></v-divider>
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field
                                    v-model.number="weightFilter"
                                    :label="$t('entries.filterWeight') + ' (t)'"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>

                            <!-- MDP (m²) -->
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field
                                    v-model.number="mdpFilter"
                                    :label="$t('entries.filterMDP') + ' (m²)'"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>
                            <!-- Expires in -->
                            <v-col cols="12" sm="6" md="3">
                                <v-select
                                    v-model="expiresInFilter"
                                    :label="$t('entries.expiresIn')"
                                    :items="expiresInOptions"
                                    item-title="label"
                                    item-value="value"
                                    clearable
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>
                        <v-row :dense="variant === 'reservations'">
                            <v-divider v-if="variant !== 'reservations'"></v-divider>
                            <template v-if="variant === 'reservations'">
                                <v-col cols="12" sm="12" md="4" lg="2">
                                    <v-text-field
                                        v-model.trim="identificationIdFilter"
                                        :label="$t('entries.filterId')"
                                        placeholder="TR-XXXXXXX / WR-XXXXXXX"
                                        density="compact"
                                        hide-details="auto"
                                        @keydown.stop
                                    />
                                </v-col>
                                <v-col cols="12" sm="12" md="4" lg="2">
                                    <v-text-field
                                        v-model.trim="transportOrderNumberFilter"
                                        :label="$t('transportOrder.transportOrderNumber')"
                                        placeholder=""
                                        density="compact"
                                        hide-details="auto"
                                        @keydown.stop
                                    />
                                </v-col>
                                <v-col cols="12" sm="12" md="4" lg="2">
                                    <v-select
                                        v-model="selectedStatuses"
                                        :items="resolvedReservationStatusOptions"
                                        item-title="label"
                                        item-value="value"
                                        multiple
                                        density="compact"
                                        hide-details="auto"
                                        :label="$t('entries.statuses')"
                                        :placeholder="$t('entries.statusesPlaceholder')"
                                        @keydown.stop
                                    >
                                        <template #selection="{ index }">
                                            <span v-if="isStatusesAllSelected && index === 0">
                                                {{ $t("entries.checkAllStatuses") }}
                                            </span>
                                        </template>
                                        <template #prepend-item>
                                            <v-list-item style="background:#123666;color:white" @click.stop="onStatusesCheckAll">
                                                <template #prepend>
                                                    <v-checkbox-btn
                                                        :model-value="isStatusesAllSelected"
                                                        density="compact"
                                                    />
                                                </template>
                                                <v-list-item-title>{{
                                                    $t("entries.checkAllStatuses")
                                                }}</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item style="background:#123666;color:white" @click.stop="onStatusesUncheckAll">
                                                <template #prepend>
                                                    <v-checkbox-btn
                                                        :model-value="isStatusesNoneSelected"
                                                        density="compact"
                                                    />
                                                </template>
                                                <v-list-item-title>{{
                                                    $t("entries.uncheckAllStatuses")
                                                }}</v-list-item-title>
                                            </v-list-item>
                                            <v-divider class="my-1" />
                                        </template>
                                        <template #item="{ props, item }">
                                            <v-list-item v-bind="props">
                                                <template #prepend>
                                                    <v-checkbox-btn
                                                        :model-value="selectedStatuses.includes(item.value)"
                                                        density="compact"
                                                    />
                                                </template>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col cols="12" sm="12" md="4" lg="2" class="d-flex flex-column flex-sm-row flex-wrap align-start align-sm-center ga-4">
                                    <v-checkbox
                                        v-model="missingDocumentsFilter"
                                        :label="$t('entries.missingDocuments')"
                                        hide-details
                                        density="compact"
                                        color="primary"
                                    />
                                </v-col>
                                <v-col cols="12" sm="12" md="4" lg="2" class="d-flex flex-column flex-sm-row flex-wrap align-start align-sm-center ga-4">
                                    <v-checkbox
                                        v-model="inNegotiationFilter"
                                        :label="$t('entries.inNegotiation')"
                                        hide-details
                                        density="compact"
                                        color="primary"
                                    />
                                </v-col>
                            </template>
                            <template v-else-if="formType === 'ware'">
                                <v-col cols="12" sm="12" md="3">
                                    <v-text-field
                                        v-model.trim="identificationIdFilter"
                                        :label="$t('entries.filterId')"
                                        placeholder="TR-XXXXXXX / WR-XXXXXXX"
                                        hide-details="auto"
                                        @keydown.stop
                                    />
                                </v-col>
                                <v-col cols="12" sm="12" md="3" class="d-flex align-center">
                                    <v-checkbox
                                        v-model="showReservedWareOnly"
                                        :label="$t('entries.showReservedWareOnly')"
                                        hide-details
                                        density="compact"
                                        color="primary"
                                    />
                                </v-col>
                            </template>
                            <v-col v-else cols="12">
                                <v-text-field
                                    v-model.trim="identificationIdFilter"
                                    :label="$t('entries.filterId')"
                                    placeholder="TR-XXXXXXX / WR-XXXXXXX"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-divider></v-divider>
                            <v-col cols="12">
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-btn color="error" block @click="resetFilters">
                                            {{ $t("entries.clearFilters") }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-btn color="primary" block @click="showResults">
                                            {{ $t("entries.showResults") }}
                                            <span v-if="typeof resultsCount === 'number'">
                                                ({{ resultsCount }})
                                            </span>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </div>
            </v-card>
        </v-col>
    </v-row>
    </div>
</template>

<script>
import FormLocation from "@/components/forms/FormLocation.vue";
import { mapState } from "vuex";

export default {
    components: { FormLocation },
    props: {
        // initial filters from the parent
        modelValue: {
            type: Object,
            default: () => ({
                loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
                showReservedWareOnly: false,
            }),
        },
        title: {
            type: String,
            default: "",
        },
        openTooltipText: {
            type: String,
            default: "Open filtering bar",
        },
        closeTooltipText: {
            type: String,
            default: "Close filtering bar",
        },
        variant: {
            type: String,
            default: "entries", // "entries" | "reservations"
        },
        expanded: {
            type: Boolean,
            default: undefined,
        },
        reservationStatusOptions: {
            type: Array,
            default: () => [],
        },
        resultsCount: {
            type: Number,
            default: null,
        },
        formType: {
            type: String,
            default: null,
        },
        sortDirection: {
            type: String,
            default: "desc",
        },
        showSortToggle: {
            type: Boolean,
            default: true,
        },
    },
    emits: ["update:modelValue", "toggle-sort", "update:expanded"],
    data() {
        return {
            loadingFilter: this.modelValue.loadingLocation || {
                country_code: null,
                place_id: null,
                place_display_name: null,
            },
            unloadingFilter: this.modelValue.unloadingLocation || {
                country_code: null,
                place_id: null,
                place_display_name: null,
            },
            internalExpanded: false,
            syncingFiltersFromParent: false,
            /** True while resetFilters() mass-assigns; suppresses watcher emits until final emitUpdate. */
            bulkFiltersUpdating: false,
            weightFilter: this.modelValue.weight ?? null,
            mdpFilter: this.modelValue.mdp ?? null,
            expiresInFilter: this.modelValue.expiresIn ?? null,
            identificationIdFilter: this.modelValue.identificationId ?? "",
            transportOrderNumberFilter: this.modelValue.transportOrderNumber ?? "",
            showReservedWareOnly: !!this.modelValue.showReservedWareOnly,
            selectedStatuses: Array.isArray(this.modelValue.statuses) ? [...this.modelValue.statuses] : [],
            missingDocumentsFilter: !!this.modelValue.missingDocuments,
            inNegotiationFilter: !!this.modelValue.inNegotiation,
        };
    },
    computed: {
        isExpanded: {
            get() {
                if (typeof this.expanded === "boolean") return this.expanded;
                return this.internalExpanded;
            },
            set(nextExpanded) {
                if (typeof this.expanded === "boolean") {
                    this.$emit("update:expanded", !!nextExpanded);
                } else {
                    this.internalExpanded = !!nextExpanded;
                }
            },
        },
        resolvedReservationStatusOptions() {
            if (this.reservationStatusOptions?.length) {
                return this.reservationStatusOptions;
            }
            const fallbackCodes = [
                "pending",
                "waiting_for_to",
                "waiting_for_signed_to",
                "waiting_for_confirmation",
                "confirmed",
                "in_progress",
                "completed_and_awaiting_confirmation",
                "completed_and_confirmed",
                "declined",
            ];
            return fallbackCodes.map((statusCode) => ({ value: statusCode, label: statusCode }));
        },
        allReservationStatusValues() {
            return this.resolvedReservationStatusOptions.map((option) => option.value);
        },
        isStatusesAllSelected() {
            const allCodes = this.allReservationStatusValues;
            if (!allCodes.length) return false;
            return this.selectedStatuses.length === allCodes.length;
        },
        isStatusesNoneSelected() {
            return this.selectedStatuses.length === 0;
        },
        expiresInOptions() {
            return [
                { value: "4h", label: this.$t("entries.expiresIn4h") },
                { value: "6h", label: this.$t("entries.expiresIn6h") },
                { value: "1d", label: this.$t("entries.expiresIn1d") },
            ];
        },
        ...mapState(["countries", "isMobile"]),
        chips() {
            const chipsList = [];

            const getCountryDisplayName = (countryCode) => {
                const country = this.countries.find((entry) => entry.code === countryCode);
                return country ? country.name : countryCode;
            };

            if (this.loadingFilter.country_code) {
                chipsList.push({
                    id: "load-country",
                    label: `${this.$t(
                        "entries.loadingFilterTitle"
                    )}: ${getCountryDisplayName(this.loadingFilter.country_code)}`,
                    clear: () => {
                        this.loadingFilter.country_code = null;
                        this.loadingFilter.place_id = null;
                        this.loadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.loadingFilter.place_display_name) {
                chipsList.push({
                    id: "load-place",
                    label: this.loadingFilter.place_display_name,
                    clear: () => {
                        this.loadingFilter.place_id = null;
                        this.loadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.unloadingFilter.country_code) {
                chipsList.push({
                    id: "unload-country",
                    label: `${this.$t(
                        "entries.unloadingFilterTitle"
                    )}: ${getCountryDisplayName(this.unloadingFilter.country_code)}`,
                    clear: () => {
                        this.unloadingFilter.country_code = null;
                        this.unloadingFilter.place_id = null;
                        this.unloadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.unloadingFilter.place_display_name) {
                chipsList.push({
                    id: "unload-place",
                    label: this.unloadingFilter.place_display_name,
                    clear: () => {
                        this.unloadingFilter.place_id = null;
                        this.unloadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.weightFilter !== null) {
                chipsList.push({
                    id: "weight",
                    label: `${this.$t("entries.weight")}: ≤ ${
                        this.weightFilter
                    } t`,
                    clear: () => (this.weightFilter = null),
                });
            }

            if (this.mdpFilter !== null) {
                chipsList.push({
                    id: "mdp",
                    label: `${this.$t("entries.mdp")}: ≤ ${this.mdpFilter} m²`,
                    clear: () => (this.mdpFilter = null),
                });
            }

            if (this.expiresInFilter) {
                const expiresOption = this.expiresInOptions.find(
                    (option) => option.value === this.expiresInFilter
                );
                chipsList.push({
                    id: "expires",
                    label: `${this.$t("entries.expiresIn")} ${expiresOption?.label || this.expiresInFilter}`,
                    clear: () => (this.expiresInFilter = null),
                });
            }

            if (this.identificationIdFilter) {
                chipsList.push({
                    id: "ident-id",
                    label: `${this.$t("entries.filterId")}: ${
                        this.identificationIdFilter
                    }`,
                    clear: () => (this.identificationIdFilter = ""),
                });
            }
            if (this.variant === "reservations" && this.transportOrderNumberFilter) {
                chipsList.push({
                    id: "to-number",
                    label: `${this.$t("transportOrder.transportOrderNumber")}: ${this.transportOrderNumberFilter}`,
                    clear: () => (this.transportOrderNumberFilter = ""),
                });
            }
            if (this.variant === "reservations") {
                const allStatusCodes = this.allReservationStatusValues;
                const everyStatusSelected =
                    allStatusCodes.length > 0 && this.selectedStatuses.length === allStatusCodes.length;
                if (!everyStatusSelected) {
                    const excludedStatusCodes = allStatusCodes.filter(
                        (code) => !this.selectedStatuses.includes(code)
                    );
                    for (const statusCode of excludedStatusCodes) {
                        const statusLabel =
                            this.resolvedReservationStatusOptions.find((option) => option.value === statusCode)
                                ?.label || statusCode;
                        chipsList.push({
                            id: `excluded-${statusCode}`,
                            label: `${this.$t("entries.excluded")}: ${statusLabel}`,
                            clear: () => {
                                const reIncluded = new Set([...this.selectedStatuses, statusCode]);
                                this.selectedStatuses = Array.from(reIncluded);
                            },
                        });
                    }
                }
            }
            if (this.formType === "ware" && this.showReservedWareOnly) {
                chipsList.push({
                    id: "reserved-ware",
                    label: this.$t("entries.showReservedWareOnly"),
                    clear: () => (this.showReservedWareOnly = false),
                });
            }
            if (this.variant === "reservations" && this.missingDocumentsFilter) {
                chipsList.push({
                    id:"missing-documents",
                    label:this.$t("entries.missingDocuments"),
                    clear:() => (this.missingDocumentsFilter = false),
                });
            }
            if (this.variant === "reservations" && this.inNegotiationFilter) {
                chipsList.push({
                    id: "in-negotiation",
                    label: this.$t("entries.inNegotiation"),
                    clear: () => (this.inNegotiationFilter = false),
                });
            }
            return chipsList;
        },
    },
    methods: {
        /** Emit filter state to parent unless we are applying a parent v-model sync (avoids loops). */
        emitFiltersToParentIfNeeded() {
            if (this.syncingFiltersFromParent || this.bulkFiltersUpdating) return;
            this.emitUpdate();
        },
        ensureStatusesInitialized() {
            if (this.variant !== "reservations") return;
            const allStatusCodes = this.allReservationStatusValues;
            if (!allStatusCodes.length) return;

            // If parent says "all statuses" (null/undefined) we explicitly select all locally,
            // so the menu checkboxes render as checked.
            if (this.modelValue?.statuses == null) {
                if (this.selectedStatuses.length !== allStatusCodes.length) {
                    this.selectedStatuses = [...allStatusCodes];
                }
            }
        },
        handleLoadingFilter(nextLoadingLocation) {
            this.loadingFilter = nextLoadingLocation;
        },
        handleUnloadingFilter(nextUnloadingLocation) {
            this.unloadingFilter = nextUnloadingLocation;
        },
        resetFilters() {
            this.bulkFiltersUpdating = true;
            try {
                this.loadingFilter = { country_code: null, place_id: null, place_display_name: null };
                this.unloadingFilter = { country_code: null, place_id: null, place_display_name: null };
                this.weightFilter = null;
                this.mdpFilter = null;
                this.expiresInFilter = null;
                this.identificationIdFilter = "";
                this.transportOrderNumberFilter = "";
                this.showReservedWareOnly = false;
                this.selectedStatuses = [...this.allReservationStatusValues];
                this.missingDocumentsFilter = false;
                this.inNegotiationFilter = false;
                this.emitUpdate();
            } finally {
                this.bulkFiltersUpdating = false;
            }
        },
        emitUpdate() {
            if (this.weightFilter === "" || Number.isNaN(this.weightFilter)) this.weightFilter = null;
            if (this.mdpFilter === "" || Number.isNaN(this.mdpFilter)) this.mdpFilter = null;

            this.$emit("update:modelValue", {
                loadingLocation: this.loadingFilter,
                unloadingLocation: this.unloadingFilter,
                weight: this.weightFilter,
                mdp: this.mdpFilter,
                expiresIn: this.expiresInFilter || null,
                identificationId: this.identificationIdFilter || null,
                transportOrderNumber: this.transportOrderNumberFilter || null,
                showReservedWareOnly: this.showReservedWareOnly,
                statuses: this.isStatusesAllSelected ? null : [...this.selectedStatuses],
                missingDocuments: this.missingDocumentsFilter,
                inNegotiation: this.inNegotiationFilter,
            });
        },
        onStatusesCheckAll() {
            this.selectedStatuses = [...this.allReservationStatusValues];
        },
        onStatusesUncheckAll() {
            this.selectedStatuses = [];
        },
        showResults() {
            this.isExpanded = false;
        },
        onClickOutside() {
            this.isExpanded = false;
        },
        clickOutsideInclude() {
            // v-select / v-autocomplete menus are teleported into .v-overlay-container,
            // and should not count as "outside" clicks for closing the filter bar.
            return Array.from(document.querySelectorAll(".v-overlay-container"));
        },
    },
    watch: {
        expanded: {
            immediate: true,
            handler(nextExpanded) {
                if (typeof nextExpanded === "boolean") this.internalExpanded = nextExpanded;
            },
        },
        modelValue: {
            deep: true,
            handler(incomingModel) {
                // When the parent replaces `modelValue`, copy fields into local state without re-emitting
                // (avoids update loops).
                this.syncingFiltersFromParent = true;
                const next = incomingModel || {};
                if (next.loadingLocation) this.loadingFilter = next.loadingLocation;
                if (next.unloadingLocation) this.unloadingFilter = next.unloadingLocation;
                if (Object.prototype.hasOwnProperty.call(next, "identificationId")) {
                    this.identificationIdFilter = next.identificationId ?? "";
                }
                if (Object.prototype.hasOwnProperty.call(next, "transportOrderNumber")) {
                    this.transportOrderNumberFilter = next.transportOrderNumber ?? "";
                }
                if (Object.prototype.hasOwnProperty.call(next, "weight")) {
                    this.weightFilter = next.weight ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(next, "mdp")) {
                    this.mdpFilter = next.mdp ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(next, "expiresIn")) {
                    this.expiresInFilter = next.expiresIn ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(next, "showReservedWareOnly")) {
                    this.showReservedWareOnly = !!next.showReservedWareOnly;
                }
                if (Object.prototype.hasOwnProperty.call(next, "statuses")) {
                    if (Array.isArray(next.statuses)) {
                        this.selectedStatuses = [...next.statuses];
                    } else {
                        this.selectedStatuses = [...this.allReservationStatusValues];
                    }
                }
                if (Object.prototype.hasOwnProperty.call(next, "missingDocuments")) {
                    this.missingDocumentsFilter = !!next.missingDocuments;
                }
                if (Object.prototype.hasOwnProperty.call(next, "inNegotiation")) {
                    this.inNegotiationFilter = !!next.inNegotiation;
                }
                this.ensureStatusesInitialized();
                this.$nextTick(() => {
                    this.syncingFiltersFromParent = false;
                });
            },
        },
        reservationStatusOptions: {
            immediate: true,
            handler() {
                this.ensureStatusesInitialized();
            },
        },
        weightFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        /** Location chips mutate nested fields via chip.clear(); sync parent like weight/expiry watchers. */
        loadingFilter: {
            deep: true,
            handler() {
                this.emitFiltersToParentIfNeeded();
            },
        },
        unloadingFilter: {
            deep: true,
            handler() {
                this.emitFiltersToParentIfNeeded();
            },
        },
        mdpFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        identificationIdFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        transportOrderNumberFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        selectedStatuses: {
            deep: true,
            handler() {
                this.emitFiltersToParentIfNeeded();
            },
        },
        showReservedWareOnly() {
            this.emitFiltersToParentIfNeeded();
        },
        expiresInFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        missingDocumentsFilter() {
            this.emitFiltersToParentIfNeeded();
        },
        inNegotiationFilter() {
            this.emitFiltersToParentIfNeeded();
        },
    },
};
</script>

<style scoped>
.filter-bar-wrap {
    position: relative;
}
.filter-card {
    position: relative;
    z-index: 2;
}
</style>
